import React, { useState, useEffect, useCallback } from 'react';
import { formatIST } from '../utils/dateUtils';
import './CampaignsPage.css';
import BroadcastModal from './BroadcastModal';
import CampaignContactsModal from './CampaignContactsModal';
import OutletBadge from './OutletBadge';

const apiUrl = process.env.REACT_APP_API_URL;
const BASE_URL = `${apiUrl}/whatsapp`;

const sampleContacts = [];

function CampaignsPage({ token, business_id, businessName }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [contacts, setContacts] = useState(sampleContacts);

  // For contact viewer modal
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [contactsModalConfig, setContactsModalConfig] = useState({
    campaignId: null,
    status: null, // "sent" | "delivered" | "read" | "failed"
  });

  // Fetch campaigns from API - using useCallback to prevent multiple calls
  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${BASE_URL}/get_campaigns`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setCampaigns(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch campaigns');
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch campaigns on component mount
  useEffect(() => {
    if (token) {
      fetchCampaigns();
    }
  }, [token, fetchCampaigns]);

  const handleModalClose = () => {
    setShowBroadcastModal(false);
    setContacts(sampleContacts);
  };

  const handleCampaignSuccess = () => {
    // Refresh campaigns after a delay to avoid 500 error
    setTimeout(() => {
      fetchCampaigns();
    }, 2000);
  };

  // Format date using IST utility
  const formatDate = (dateString) => formatIST(dateString, 'short');

  // Open contacts modal for a given status
  const openContactsModal = (campaignId, status) => {
    setContactsModalConfig({ campaignId, status });
    setContactsModalOpen(true);
  };

  // When user selects contacts in the contacts modal and clicks "Broadcast Selected"
  const handleContactsSelectedForBroadcast = (selectedContacts) => {
    // Close contacts modal
    setContactsModalOpen(false);

    // Feed selected contacts into BroadcastModal
    setContacts(selectedContacts || []);
    setShowBroadcastModal(true);
  };

  const statusColor = (status) => {
    if (status === 'completed') return 'green';
    if (status === 'failed') return 'red';
    return 'orange';
  };

  return (
    <div className="campaigns-page-container">
      <div className="campaigns-header-bar">
        <div className="campaigns-title-outlet-group">
          <h2 style={{ margin: 0 }}>Campaigns</h2>
          <OutletBadge businessName={businessName} businessId={business_id} pageTitle="Campaigns" />
        </div>
        <button
          className="broadcast-button"
          onClick={() => setShowBroadcastModal(true)}
          style={{ margin: 0 }}
        >
          Create New Broadcast
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading campaigns...
        </div>
      )}

      {error && (
        <div
          style={{
            color: 'red',
            padding: '10px',
            marginBottom: '20px',
            background: '#ffe6e6',
            borderRadius: '4px'
          }}
        >
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <table className="campaigns-table" style={{ width: '100%', marginBottom: 24 }}>
          <thead>
            <tr>
              <th>Campaign Name</th>
              <th>Template</th>
              <th>Status</th>
              <th>Recipients</th>
              <th>Success</th>
              <th>Failed</th>
              {/* New columns */}
              <th>Sent</th>
              <th>Delivered</th>
              <th>Read</th>
              <th>FB Failed</th>
              <th>Created (+5:30)</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map(campaign => (
              <CampaignRow
                key={campaign.request_id}
                campaign={campaign}
                statusColor={statusColor}
                formatDate={formatDate}
                openContactsModal={openContactsModal}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Contacts viewer + selection modal */}
      <CampaignContactsModal
        show={contactsModalOpen}
        onClose={() => setContactsModalOpen(false)}
        token={token}
        campaignId={contactsModalConfig.campaignId}
        status={contactsModalConfig.status}
        onBroadcastSelected={handleContactsSelectedForBroadcast}
      />

      {/* Existing broadcast modal.
          Per Option B, the user will choose a new template/campaign. We only prefill contacts. */}
      <BroadcastModal
        token={token}
        show={showBroadcastModal}
        onClose={handleModalClose}
        initialContacts={contacts}
        onSuccess={handleCampaignSuccess}
        showCsvImport={true}
      />
    </div>
  );
}

const CampaignRow = ({ campaign, statusColor, formatDate, openContactsModal }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <tr className={expanded ? 'expanded' : ''}>
      <td data-label="Campaign Name">{campaign.campaign_name}</td>
      <td data-label="Template">{campaign.template_name}</td>
      <td data-label="Status" style={{
        color: statusColor(campaign.status),
        fontWeight: 'bold'
      }}>
        {campaign.status}
      </td>
      <td data-label="Recipients">{campaign.total_recipients}</td>

      <td className="mobile-hidden" data-label="Success" style={{ color: 'green' }}>{campaign.success_count}</td>
      <td className="mobile-hidden" data-label="Failed" style={{ color: 'red' }}>{campaign.failed_count}</td>

      <td className="mobile-hidden" data-label="Sent">
        <button
          style={{ background: 'none', border: 'none', color: '#0b5ed7', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => openContactsModal(campaign.campaign_id, 'sent')}
          title="View sent contacts"
        >
          {campaign.message_sent ?? 0}
        </button>
      </td>
      <td className="mobile-hidden" data-label="Delivered">
        <button
          style={{ background: 'none', border: 'none', color: '#0b5ed7', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => openContactsModal(campaign.campaign_id, 'delivered')}
          title="View delivered contacts"
        >
          {campaign.message_delivered ?? 0}
        </button>
      </td>
      <td className="mobile-hidden" data-label="Read">
        <button
          style={{ background: 'none', border: 'none', color: '#0b5ed7', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => openContactsModal(campaign.campaign_id, 'read')}
          title="View read contacts"
        >
          {campaign.message_read ?? 0}
        </button>
      </td>
      <td className="mobile-hidden" data-label="FB Failed">
        <button
          style={{ background: 'none', border: 'none', color: '#0b5ed7', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => openContactsModal(campaign.campaign_id, 'failed')}
          title="View FB failed contacts"
        >
          {campaign.message_failed ?? 0}
        </button>
      </td>
      <td className="mobile-hidden" data-label="Created (+5:30)">{formatDate(campaign.created_at)}</td>

      <td className="mobile-only view-more-cell" onClick={() => setExpanded(!expanded)}>
        {expanded ? 'Show Less' : 'View More'}
      </td>
    </tr>
  );
};

export default CampaignsPage;
